import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Filter, AlertTriangle } from 'lucide-react';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { PatientCertificateDoc } from '../types';
import CertificateCard from '../components/CertificateCard';
import NHCIdBadge from '../components/NHCIdBadge';
import InstitutionShareModal from '../components/InstitutionShareModal';
import Spinner from '../../../components/ui/Spinner';

/**
 * Filters that are about STATE rather than kind. The kind chips are built from
 * the server's own type catalogue (`GET /certificates/types`) — this page used
 * to hardcode nine of them, offered none for MEDICAL_CERTIFICATE (which is
 * what every doctor-signed fitness certificate is, so they were unreachable),
 * and its sibling label map named six types this platform does not issue.
 */
const STATE_FILTERS = [
  { key: 'expired', label: 'Expired' },
  { key: 'revoked', label: 'Revoked' },
  { key: 'pending', label: 'Pending Verify' },
  { key: 'verified', label: 'Verified' },
];

export default function CertificatesCenter() {
  const navigate = useNavigate();
  const [certs, setCerts] = useState<PatientCertificateDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [nhcId, setNhcId] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [shareTarget, setShareTarget] = useState<PatientCertificateDoc | null>(null);
  const [types, setTypes] = useState<{ type: string; label: string }[]>([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // Independent catches — a certificates failure must not hide the NHC ID
    // badge (profile), and vice versa.
    Promise.all([
      patientApi.getCertificates().catch(() => null),
      patientApi.getProfile().catch(() => null),
      patientApi.getCertificateTypes().catch(() => []),
    ])
      .then(([c, p, t]) => {
        if (!alive) return;
        if (c) { setCerts(c); setLoadError(false); } else { setLoadError(true); }
        if (p) setNhcId(p.nhcId);
        setTypes(t ?? []);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  /**
   * Kind chips, offered only for types this patient actually holds — the
   * catalogue names all fourteen, and a chip that can only ever be empty is
   * noise on a screen with nine of them.
   */
  const heldTypes = new Set(certs.map(c => c.certificateType));
  const FILTERS = [
    { key: 'all', label: 'All' },
    ...types.filter(t => heldTypes.has(t.type)).map(t => ({ key: t.type, label: t.label })),
    ...STATE_FILTERS,
  ];

  const filtered = certs.filter(c => {
    if (filter === 'all') return true;
    // The server decides this from the date, so a record still marked ACTIVE
    // with a lapsed expiry is counted as expired on both clients alike.
    if (filter === 'expired') return c.status === 'EXPIRED' || c.expired === true;
    if (filter === 'revoked') return c.status === 'REVOKED';
    if (filter === 'pending') return c.verificationStatus === 'PENDING_NEOVERIFY';
    if (filter === 'verified') return c.verificationStatus === 'VERIFIED';
    return c.certificateType === filter;
  });

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-6" style={govPageContainer()}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2" style={GOV_TYPE.pageTitle}>
            <Award className="w-6 h-6 text-teal-600" /> Certificate Center
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Official certificates linked to your NeoHealthCard</p>
        </div>
        {nhcId && <NHCIdBadge nhcId={nhcId} />}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="w-4 h-4 text-gray-400 shrink-0" />
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${
              filter === f.key ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Info note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex gap-2 text-xs text-blue-800">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <p>NeoVerify proves document authenticity. It is <strong>not the same</strong> as health data consent. Sharing a certificate does not expose your full medical records.</p>
      </div>

      {/* Cards */}
      {loadError ? (
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-red-200 mx-auto mb-3" />
          <p className="text-red-500 text-sm">Could not load your certificates. Please refresh the page.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Award className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">
            {certs.length === 0
              ? 'No certificates yet — certificates issued by your doctors, hospitals, and labs will appear here.'
              : 'No certificates found for this filter'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(c => (
            <CertificateCard key={c.id} cert={c} onShare={setShareTarget} onView={(cert) => navigate(`/patient/certificates/preview/${cert.id}`)} />
          ))}
        </div>
      )}

      <p style={GOV_COMPLIANCE.text}>NeoHealthCard™ · Certificates are provider-issued · Every access is audit logged</p>

      {shareTarget && (
        <InstitutionShareModal
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          certificateId={shareTarget.id}
          certificateTitle={shareTarget.title}
        />
      )}
    </div>
  );
}
